// #1583 — ONE implementation of "what could this member have attended, and did
// they?".
//
// Three surfaces used to answer that question and no two of them agreed: the
// Member Performance Review counted graded training types by clustered
// opportunity and prorated every denominator for the member's active window;
// the profile Incidents tab divided by the department's month totals and
// prorated for nothing; the profile Training tab deduped practices by day. A
// member could read three different "attendance" numbers about themselves in
// one sitting. The review's rules win — they are the ones reasoned about,
// documented on the page they print, and signed into a personnel file — and
// they live here so every surface counts the same way.
//
// **The one rule, stated once:** an event counts as *possible* when it falls
// inside the member's active intervals AND the department didn't have them away
// on a deployment or time off — unless they turned out anyway, which always
// counts. Everything else (leave, before joining, after leaving) drops the
// event outright, helping and hurting the percentage equally.
//
// Pure module in the house style of memberStatus.js / trainingAttendance.js: no
// firebase, no `src/` imports, so the Cloud Run api and the frontend can both
// run it. Deliberately does NOT require ../index.js — index requires this file,
// and a cycle would hand one side a half-built module. Anything from the
// catalog (TrainingTypes labels, AttendanceTrainingTypes, isCountableIncident)
// is the CALLER's to apply and pass in.

const moment = require('moment');
const {timeOffCovering} = require('./timeOff');

const {intervalsContain, toDateValue} = require('../memberStatus');
const {trainingAttendeeUids} = require('../trainingAttendance');

const DAY_MS = 86400000;

// ---- deployments ---------------------------------------------------------
// Moved here from embertracking/src/pages/deployment/deploymentMembers.js
// (#1567) unchanged in behaviour: the api can't import from `src/`, and the
// deployment rule is half of the attendance rule.

// Every uid staffed on one rotation (crewLeader + spCrewMembers +
// apparatusMembers) — the single place that knows where a rotation keeps its
// crew, so no derivation of "who was on this deployment" can drift.
const rotationCrewIds = rotation => [
    rotation?.crewLeader,
    ...(rotation?.spCrewMembers || []),
    ...(rotation?.apparatusMembers || [])
].filter(Boolean);

// A deployment's apparatus entry normally points at a doc in our own fleet
// (`apparatusId`), but crews are regularly sent out on a regional/mutual-aid
// resource we don't own and can't add to inventory. Those entries store the
// `'other'` sentinel plus a free-text `apparatusName`.
//
// Lives here rather than only in `src/pages/deployment/apparatusLabel.js`
// because the report models need to name a rig too, and they can't import from
// `src/` — that file re-exports these.
const OTHER_APPARATUS_ID = 'other';

const isOtherApparatus = entry => (entry ? entry.apparatusId === OTHER_APPARATUS_ID : false);

// Tags are free text and are routinely saved with stray whitespace — Sicamous
// had 6 of 10 carrying a trailing space ("Sicamous Engine 8 "). Comparing a
// typed name against a raw tag therefore never matched, so the picker recorded
// the crew's own engine as an external resource and it never read as deployed
// (#1526). Every tag comparison goes through this; never compare tags directly.
const normalizeTag = value => (value || '').trim().replace(/\s+/g, ' ').toLowerCase();

// The fleet apparatus a typed name refers to, or null.
const findApparatusByTag = (apparatus, text) => {
    const wanted = normalizeTag(text);
    if (!wanted) return null;
    return (apparatus || []).find(doc => normalizeTag(doc && doc.tag) === wanted) || null;
};

// Display label for an apparatus entry, from either source. Used at every tag
// render site so external resources never show up as "Unknown".
const apparatusEntryLabel = (entry, apparatusById) => {
    const tag = entry && entry.apparatusId && apparatusById ? (apparatusById[entry.apparatusId] || {}).tag : null;
    return tag || (entry && entry.apparatusName) || 'Unknown apparatus';
};

// `{uid: [{start, end, deploymentId, incidentNumber, incidentName}]}` — WHEN
// each member was away, not just whether.
//
// Every window is a ROTATION's, never the deployment's — a deployment outlives
// 14 days across replacement crews, so a member's days away are the length of
// their own rotation (#1427). An open-ended rotation is still running, so it
// runs to today, the same reading the deployments grid's Active Crew column
// uses.
//
// Deployments never touch `users/{uid}/statusChanges`, which is activeIntervals'
// only input — so without this a member sent to a wildfire for two weeks reads
// as somebody who skipped two practice nights and a fortnight of calls.
const deploymentIntervalsByMember = (deployments, now) => {
    const byUid = {};

    for (const deployment of deployments || []) {
        for (const entry of deployment?.apparatus || []) {
            for (const rotation of entry?.rotations || []) {
                if (!rotation?.startDate) continue;

                const start = moment(toDateValue(rotation.startDate));
                const end = rotation.endDate
                    ? moment(toDateValue(rotation.endDate))
                    : moment(now || undefined);
                if (!start.isValid() || !end.isValid()) continue;

                // One object shared by every member of the rotation — they were
                // all away on the same days, on the same fire.
                const window = {
                    start,
                    end,
                    deploymentId: deployment.id || null,
                    incidentNumber: deployment.incidentNumber || null,
                    incidentName: deployment.incidentName || null
                };

                for (const uid of rotationCrewIds(rotation)) {
                    (byUid[uid] = byUid[uid] || []).push(window);
                }
            }
        }
    }

    return byUid;
};

// The deployment window covering a given day, or null. Day-granular and
// inclusive of both ends: the travel day home is still a day the member spent
// on the fire, not at the hall.
const deploymentCovering = (intervals, date) => {
    if (!date) return null;
    const day = moment(toDateValue(date));
    if (!day.isValid()) return null;
    return (intervals || []).find(i => !day.isBefore(i.start, 'day') && !day.isAfter(i.end, 'day')) || null;
};

// The fourth way to be away, and the only one the department chose: it sent
// them. Named rather than left as a bare "On deployment" — on a signed review
// the incident is the evidence, and an officer reading the row shouldn't have
// to go and look up which fire it was.
const deploymentReason = deployment => {
    const label = [deployment.incidentNumber, deployment.incidentName].filter(Boolean).join(' ');
    return label ? `On deployment — ${label}` : 'On deployment';
};

// Why an event fell outside a member's active window — or null when it fell
// outside in a way that isn't worth listing.
//
// A leave is worth listing: the member was on the roster and the event was
// theirs to miss, so naming it tells them what to catch up on. An event held
// before they joined, or after they left, never was theirs — and the active
// window line ("Joined Jul 1") already accounts for the gap it leaves in the
// denominator. Listing them buried a mid-year joiner's real rows under a page
// of practices held before they were a member.
const awayReason = (date, joinedAt, leftAt) => {
    const at = toDateValue(date);
    if (joinedAt && at && at < toDateValue(joinedAt)) return null;
    if (leftAt && at && at >= toDateValue(leftAt)) return null;
    return 'On leave';
};

// ---- training clustering -------------------------------------------------
// Moved here from src/pages/reports/models.js, unchanged in behaviour.

// Sessions that RAN AT THE SAME TIME are one opportunity, not several. A
// department with two halls often splits a practice night across both and files
// a record for each; a member can only be at one of them, so counting two would
// halve their attendance for turning out exactly as asked. Sessions held one
// after another on the same evening stay separate — those a member could have
// attended both of.
//
// A session with no usable start/end time occupies its whole day, so it merges
// with anything else that day. That's the conservative reading: without times we
// can't tell concurrent from sequential, and over-counting the denominator is
// the error that penalises a member.
const sessionInterval = session => {
    const start = toDateValue(session.startTime) || toDateValue(session.date);
    if (!start) return null;

    const end = toDateValue(session.endTime);
    if (end && end.getTime() > start.getTime()) {
        return {start: start.getTime(), end: end.getTime()};
    }
    // No end time, but a duration will do just as well.
    const hours = Number(session.hours);
    if (Number.isFinite(hours) && hours > 0) {
        return {start: start.getTime(), end: start.getTime() + hours * 3600000};
    }
    // Neither: fall back to the whole day.
    const midnight = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
    return {start: midnight, end: midnight + DAY_MS};
};

// Merge overlapping sessions into clusters — the classic interval sweep, so
// overlap is transitive (A overlaps B, B overlaps C => one opportunity). Called
// per type, since the grouped table needs each opportunity to belong to one
// type row; two halls running the same practice are the same type anyway.
const clusterConcurrentSessions = sessions => {
    const dated = (sessions || [])
        .map(session => ({session, interval: sessionInterval(session)}))
        .filter(entry => entry.interval)
        .sort((a, b) => a.interval.start - b.interval.start);

    const clusters = [];
    dated.forEach(({session, interval}) => {
        const open = clusters[clusters.length - 1];
        if (open && interval.start < open.end) {
            open.sessions.push(session);
            open.end = Math.max(open.end, interval.end);
            return;
        }
        clusters.push({sessions: [session], date: session.date, end: interval.end});
    });

    return clusters;
};

const clusterAttendedBy = (cluster, uid) =>
    (cluster?.sessions || []).some(session => trainingAttendeeUids(session).includes(uid));

// ---- incidents -----------------------------------------------------------

// Every category the department can record a member in on an incident. The
// lists hold uid strings before populateUsers() runs and user objects after, so
// both shapes are normalized here.
const memberUids = list => (list || []).map(entry => (entry && entry.uid) || entry).filter(Boolean);

const involvementFor = (incident, uid) => ({
    responded: memberUids(incident.users).includes(uid),
    standby: memberUids(incident.standby).includes(uid),
    cancelled: memberUids(incident.cancelled).includes(uid)
});

const ALL_CATEGORIES = {responded: true, standby: true, cancelled: true};

// A station is stored as its id, but older member docs and populated rows carry
// the station object — read the id off either shape, or null for no station.
const stationIdOf = value => (value && typeof value === 'object' ? value.uid || value.id : value) || null;

// The member's home station, the one whose area they answer.
const memberStationId = user => stationIdOf(user && user.station);

const emptyIncidentBucket = () => ({possible: 0, responded: 0, standby: 0, cancelled: 0, any: 0, included: 0});

// How this member did against the incidents they could have answered.
//
// `incidents` must already be narrowed to what counts department-wide —
// isCountableIncident (Approved & Locked) and the reporting range — because
// "which incidents are real" is a catalog question, not an attendance one.
//
// `station` is the member's home station (memberStationId). An incident
// assigned to ANOTHER station's area was never theirs to answer, so a member who
// didn't turn out for it drops it from both columns, the same way leave does. An
// incident with no area set, or a member with no home station, counts as it
// always has — the area field is optional, and leaving it blank must not quietly
// take calls out of anybody's denominator.
//
// `inclusion` scopes the NUMERATOR only ({responded, standby, cancelled}
// booleans, all three by default). The deployment escape hatch deliberately
// ignores it and tests "did the department record them on this call at all":
// whether a deployment excuses a call must not depend on the position of a
// toolbar checkbox, or the review's denominator and the profile's would differ
// for a member on standby during their own rotation — exactly the class of
// disagreement this module exists to end.
const memberIncidentAttendance = (incidents, options = {}) => {
    const {uid, intervals = [], deploymentIntervals = [], timeOffIntervals = [], station = null, inclusion = ALL_CATEGORIES} = options;
    const homeStation = stationIdOf(station);

    const possibleIncidents = [];
    const involvedIncidents = [];
    const attendedIncidents = [];
    const excluded = [];
    let onTimeOff = 0;
    let outsideArea = 0;
    const byType = {};

    const totals = emptyIncidentBucket();

    (incidents || []).forEach(incident => {
        if (!incident) return;
        // Outside their active window: never theirs to answer, dropped from both
        // columns so time away neither helps nor hurts.
        if (!intervalsContain(intervals, incident.date)) return;

        const involvement = involvementFor(incident, uid);
        const any = involvement.responded || involvement.standby || involvement.cancelled;

        // Another station's call. Checked before time off and deployment: it
        // was never this member's call, whether they were home or not. Turning
        // out for it anyway still counts, like every other escape hatch here.
        const area = stationIdOf(incident.stationArea);
        if (homeStation && area && area !== homeStation && !any) {
            outsideArea++;
            excluded.push({id: incident.id || incident.uid || null, date: incident.date || null, stationArea: area});
            return;
        }

        const timeOff = timeOffCovering(timeOffIntervals, incident.dispatchTime || incident.date);
        if (timeOff && !any) {
            onTimeOff++;
            excluded.push({id: incident.id || incident.uid || null, date: incident.date || null, timeOff: {id: timeOff.id, reason: timeOff.reason}});
            return;
        }

        const deployment = deploymentIntervals.length
            ? deploymentCovering(deploymentIntervals, incident.date)
            : null;

        // The department had them 400km away on a fire it sent them to. Not a
        // call they could have answered — unless they turned out anyway, which
        // always counts.
        if (deployment && !any) {
            excluded.push({
                id: incident.id || incident.uid || null,
                date: incident.date || null,
                type: incident.type || null,
                incidentNumber: incident.incidentNumber || null,
                deployment: {
                    id: deployment.deploymentId || null,
                    incidentNumber: deployment.incidentNumber || null,
                    incidentName: deployment.incidentName || null
                }
            });
            return;
        }

        const included = Boolean(
            (inclusion.responded && involvement.responded) ||
            (inclusion.standby && involvement.standby) ||
            (inclusion.cancelled && involvement.cancelled));

        possibleIncidents.push(incident);
        if (any) involvedIncidents.push(incident);
        if (included) attendedIncidents.push(incident);

        // Keyed by the raw type, exactly as the report's own type ordering keys
        // it — an untyped incident buckets under the same key on both sides
        // instead of splitting the denominator in two.
        const bucket = byType[incident.type] = byType[incident.type] || emptyIncidentBucket();

        [totals, bucket].forEach(target => {
            target.possible += 1;
            if (involvement.responded) target.responded += 1;
            if (involvement.standby) target.standby += 1;
            if (involvement.cancelled) target.cancelled += 1;
            if (any) target.any += 1;
            if (included) target.included += 1;
        });
    });

    return {
        possible: totals.possible,
        attended: {
            responded: totals.responded,
            standby: totals.standby,
            cancelled: totals.cancelled,
            any: totals.any,
            included: totals.included
        },
        // Calls dropped because the department had them away. The number a
        // review has to print beside a moved percentage, so the reader can see
        // what happened to the difference.
        onDeployment: excluded.filter(row => row.deployment).length,
        onTimeOff,
        // Calls left out because they were in another station's area.
        outsideArea,
        byType,
        excluded,
        // The incidents behind the counts, for callers that need to roll up
        // something else over exactly the same set (hours, apparatus roles) or
        // to filter a grid so it matches its card.
        possibleIncidents,
        involvedIncidents,
        attendedIncidents
    };
};

// ---- training ------------------------------------------------------------

const dateLabelFor = date => {
    const value = toDateValue(date);
    return value ? moment(value).format('MMM D, YYYY') : '—';
};

// How this member did against the training opportunities they could have turned
// out for.
//
// `clustersByType` is `{[type]: [cluster]}` from clusterConcurrentSessions,
// already narrowed to the graded types (AttendanceTrainingTypes) by the caller —
// same division of labour as the incident half.
const memberTrainingAttendance = (clustersByType, options = {}) => {
    const {
        uid,
        intervals = [],
        deploymentIntervals = [],
        timeOffIntervals = [],
        joinedAt = null,
        leftAt = null,
        types,
        trainingTypeLabel = type => type
    } = options;

    const clusters = clustersByType || {};
    const orderedTypes = types || Object.keys(clusters);

    const deployedFor = cluster =>
        (deploymentIntervals.length ? deploymentCovering(deploymentIntervals, cluster.date) : null);

    // Whether a session counts toward this member's turnout: it has to have
    // fallen inside their active window, and the department must not have had
    // them away on a deployment that day.
    //
    // Turning out anyway always counts, which is why attendance is part of the
    // test rather than a separate rule — a member who made the practice on their
    // travel day home should never lose the credit for it.
    const timeOffFor = cluster => timeOffCovering(timeOffIntervals, cluster.sessions?.[0]?.startTime || cluster.date);
    const countsFor = cluster => intervalsContain(intervals, cluster.date) &&
        ((!deployedFor(cluster) && !timeOffFor(cluster)) || clusterAttendedBy(cluster, uid));

    const byType = {};
    const missed = [];
    const missedWhileAway = [];
    let possible = 0;
    let attended = 0;
    let onDeployment = 0;
    let onTimeOff = 0;

    orderedTypes.forEach(type => {
        const bucket = byType[type] = {possible: 0, attended: 0};

        (clusters[type] || []).forEach((cluster, index) => {
            const wasThere = clusterAttendedBy(cluster, uid);

            if (countsFor(cluster)) {
                bucket.possible += 1;
                possible += 1;
                if (wasThere) {
                    bucket.attended += 1;
                    attended += 1;
                }
            } else if (intervalsContain(intervals, cluster.date) && timeOffFor(cluster)) {
                onTimeOff += 1;
            } else if (intervalsContain(intervals, cluster.date) && deployedFor(cluster)) {
                // Inside their window, dropped only because the department had
                // them away — the training counterpart of `excluded` above.
                onDeployment += 1;
            }

            if (wasThere) return;

            const [session] = cluster.sessions;
            const concurrent = cluster.sessions.length - 1;
            const title = session.title || trainingTypeLabel(type);
            // The sessions behind the percentage, named and dated. A rate tells
            // an officer there's something to talk about; this is what they talk
            // about. One row per missed opportunity, not per record.
            const row = {
                id: session.id || `${type}-${index}`,
                date: cluster.date,
                dateLabel: dateLabelFor(cluster.date),
                // Named so a member reading it can tell a missed night from a
                // missed hall — the row stands for the whole slot.
                title: concurrent ? `${title} (+${concurrent} at the same time)` : title,
                type,
                label: trainingTypeLabel(type)
            };

            if (countsFor(cluster)) {
                missed.push(row);
                return;
            }

            // Explicit time off takes precedence over deployment and roster reasons.
            // Carry the reason structurally so reports can count each separately.
            const deployment = deployedFor(cluster);
            const timeOff = timeOffFor(cluster);
            const reason = timeOff ? `Time off — ${timeOff.reason}` : deployment
                ? deploymentReason(deployment)
                : awayReason(cluster.date, joinedAt, leftAt);
            // No reason worth stating (before they joined, after they left)
            // means the session belongs on neither list — it is not theirs to
            // have missed, and it already counts for nothing.
            if (!reason) return;

            missedWhileAway.push({
                ...row,
                reason,
                timeOff: timeOff ? {id: timeOff.id, reason: timeOff.reason} : null,
                deployment: !timeOff && deployment
                    ? {
                        id: deployment.deploymentId,
                        incidentNumber: deployment.incidentNumber,
                        incidentName: deployment.incidentName
                    }
                    : null
            });
        });
    });

    const byDate = (a, b) => (toDateValue(a.date) || 0) - (toDateValue(b.date) || 0);
    missed.sort(byDate);
    missedWhileAway.sort(byDate);

    return {possible, attended, onDeployment, onTimeOff, byType, missed, missedWhileAway};
};

module.exports = {
    OTHER_APPARATUS_ID,
    isOtherApparatus,
    normalizeTag,
    findApparatusByTag,
    apparatusEntryLabel,
    rotationCrewIds,
    deploymentIntervalsByMember,
    deploymentCovering,
    deploymentReason,
    awayReason,
    clusterConcurrentSessions,
    clusterAttendedBy,
    memberStationId,
    memberIncidentAttendance,
    memberTrainingAttendance
};
